import { forwardRef } from 'react'
import type { ButtonHTMLAttributes } from 'react'
import { cn } from '@/lib/cn'
import { ICON_SIZE, ICON_STROKE, type LucideIcon } from '@/components/icons'

/** An icon-only control. */
export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: LucideIcon
  /** Required. Becomes aria-label and the native tooltip. */
  label: string
  size?: 'sm' | 'md'
  variant?: 'ghost' | 'secondary' | 'danger'
}

const VARIANTS = {
  ghost: 'text-muted hover:bg-hover hover:text-text',
  secondary: 'border border-line bg-surface text-text hover:bg-hover',
  danger: 'text-danger hover:bg-danger-bg',
} as const

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon: Icon, label, size = 'md', variant = 'ghost', className, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex items-center justify-center rounded-md transition-colors duration-150',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        'disabled:cursor-not-allowed disabled:opacity-40',
        size === 'sm' ? 'h-7 w-7' : 'h-9 w-9',
        VARIANTS[variant],
        className,
      )}
      {...props}
    >
      <Icon size={size === 'sm' ? ICON_SIZE.xs : ICON_SIZE.sm} strokeWidth={ICON_STROKE} aria-hidden />
    </button>
  )
})
